import * as React from 'react'

import { createContext } from '~logic/lib/react'

import { FG_COLORS, FG_COLORS_TRANSPARENT } from './defaultTheme'
import { useTheme } from './ThemeProvider'
import { TColorKey, TColorShade, TExtendedColorKey, TTheme } from './typings'
import { canReverseShade, getContrastShadeFor, reverseShade } from './utils/colorManipulationUtils'
import { getColorValue, isDisabledColor } from './utils/themeColorUtils'

type TFgColors = typeof FG_COLORS
type TDividerColors = typeof FG_COLORS_TRANSPARENT

type TContextValue = {
  bgColor: TColorKey
  bgColorValue: string
  isDisabledState: boolean
  fgColorShade: TColorShade
  fgColors: TFgColors[TColorShade]
  dividerColors: TDividerColors[TColorShade]
}

const [Provider, useBgColorCtx] = createContext<TContextValue>(`BgColor`)

type TRenderChildrenFunction = (bgColorContext: TContextValue) => React.ReactNode

type TProps = {
  /**
   * accepts classic JSX children or render children function
   * which would receive current context value as first param
   */
  children: React.ReactNode | TRenderChildrenFunction
  /**
   * the color of the background (used for text color detection),
   * one of the project color keys
   */
  bgColor: TColorKey
  /**
   * an option to indicate that the element is in disabled state,
   * this will lead to potential fg color shade flip to lower contrast,
   * which is desired behavior for disabled elements
   *
   * this logic is applied automatically for colors
   * that has `disabled` string in their name
   */
  isDisabledState?: boolean
}

/**
 * Provides:
 *  - `bgColorValue` and the original `bgColor` key
 *  - shade used for the fg colors
 *  - text and divider colors based for that shade
 *  - boolean flag indicating if the element is in disabled state
 *
 * The provided value `HAS` to be defined – consider using `BgColorProvider`
 * if you need an option for empty value as well.
 * This version is more useful as a `top-level` app provider.
 *
 * There is the `useBgColorCtx` hook to consume the context value.
 */
export const DefinedBgColorProvider: React.FC<TProps> = ({
  bgColor,
  children,
  isDisabledState = false,
}) => {
  const theme = useTheme()

  const contextValue = React.useMemo<TContextValue>(() => {
    return buildColorContext(theme)(bgColor, isDisabledState)
  }, [bgColor, theme, isDisabledState])

  return <Provider value={contextValue}>{renderChildren(contextValue)(children)}</Provider>
}

type TBgColorProviderProps = OmitSafe<TProps, 'bgColor'> & {
  bgColor: Nullable<TExtendedColorKey>
}

/**
 * Provides:
 *  - text and divider colors based on set background color.
 *  - `bgColorValue` and the original `bgColor` key
 *
 * If the value is not defined or is one of non-bg-changing colors (transparent, inherit)
 * the provider will read it’s closes ancestor with the same context.
 * ! This can’t be used as a top-level provider, render at least one `DefinedBgColorProvider` above!
 *
 * There is the `useBgColorCtx` hook to consume the context value.
 */
export const BgColorProvider: React.FC<TBgColorProviderProps> = ({
  bgColor,
  children,
  isDisabledState = false,
}) => {
  const theme = useTheme()
  const higherContext = useBgColorCtx()

  const contextValue = React.useMemo<TContextValue>(() => {
    if (bgColor === null) return reuseHigherContext(theme)(higherContext, isDisabledState)
    if (bgColor === 'inherit') return reuseHigherContext(theme)(higherContext, isDisabledState)
    if (bgColor === 'transparent') return reuseHigherContext(theme)(higherContext, isDisabledState)

    return buildColorContext(theme)(bgColor, isDisabledState)
  }, [bgColor, isDisabledState, higherContext, theme])

  return <Provider value={contextValue}>{renderChildren(contextValue)(children)}</Provider>
}

export { useBgColorCtx }

// * HELPERS

// gets bg color value based on provided theme key,
// decides which fg colors (text, icon, divider) should be used on such bg
const buildColorContext = (theme: TTheme) => (
  bgColor: TProps['bgColor'],
  isDisabledState: boolean = false,
): TContextValue => {
  const bgColorValue = getColorValue(theme)(bgColor)
  // by default fg colors with higher contrast are used on any bg
  const defaultFgColorShade = getContrastShadeFor(bgColorValue)
  // if the bg color is a _disabled_ one (based on the theme key) or its said that the element is in disabled state,
  // use fg colors with lower contrast (UI thing) – but not for colors that are to close to that reversed shade
  const isDisabledColorOrState = isDisabledColor(bgColor) || isDisabledState
  const shouldFlipShade = isDisabledColorOrState && canReverseShade(bgColorValue)
  const fgColorShade = shouldFlipShade ? reverseShade(defaultFgColorShade) : defaultFgColorShade

  // select correct fg colors based on the bg shade
  const fgColors = FG_COLORS[fgColorShade]
  const dividerColors = FG_COLORS_TRANSPARENT[fgColorShade]

  return {
    bgColor,
    fgColors,
    bgColorValue,
    fgColorShade,
    dividerColors,
    isDisabledState: isDisabledColorOrState,
  }
}

// either renders the JSX children or calls the render children function
// if children are of a function type
const renderChildren = (context: TContextValue) => (children: TProps['children']) => {
  return typeof children === 'function' ? <>{children(context)}</> : <>{children}</>
}

// returns provided higher context if it can be used,
// otherwise it builds a new context based on the higher context
const reuseHigherContext = (theme: TTheme) => (
  higherContext: TContextValue,
  isDisabledState: boolean,
) => {
  // if both context are either for disabled element or not, we can reuse the higher context
  if (higherContext.isDisabledState === isDisabledState) {
    return higherContext
  }

  return buildColorContext(theme)(higherContext.bgColor, isDisabledState)
}
